<template>
  <transition duration="300" name="fade" appear>
    <b-container fluid id="members" class="page">
      <b-row>
        <b-col sm="8">
          <h2>Demonstration Builder</h2>
        </b-col>
        <b-col sm="4">
          <div v-if="form.currPage < 3" class="skip-section">
            <h6>Do you have a demonstration ready?</h6>
            <b-button @click="skipToUpload" class="button primary"
              >Upload</b-button
            >
          </div>
        </b-col>
      </b-row>

      <b-progress
        role="progressbar"
        aria-label="carousel progress bar"
        class="step-progress"
        height="4px"
        :value="((form.currPage - 1) / (form.numPages - 1)) * 100"
      ></b-progress>

      <div v-if="form.currPage === 1" class="mt-4">
        <div>
          <h4>
            This is a process to help with the creation of interactive
            demonstrations.
          </h4>
          <p class="mt-4">
            It guides users through a sequence of steps that should culminate in
            a functional demonstration project using the
            <a
              href="https://streamlit.io/"
              target="_blank"
              rel="noopener noreferrer"
              >Steamlit</a
            >
            library.
          </p>
        </div>

        <div class="separator-margin">
          <h5>Notes</h5>
          <p class="important mt-4">
            Read the text below each field. It explains the purpose of the field
            and its impact on the demonstration.
          </p>
          <p class="important">
            Some fields have validation. In order to submit, all fields must be
            valid.
          </p>
          <p class="important">
            In order to submit a demonstration, you must also be logged in. If
            you aren't logged in, click
            <a href="/admin" target="_blank" rel="noopener noreferrer">here</a>.
          </p>
          <p class="important">
            Although this helper generates some source code to expedite the
            creation and deployment of a new demo, it may be changed entirely as
            long as the template project structure is followed.
          </p>
        </div>
      </div>

      <b-row v-if="form.currPage === 1" class="separator-margin">
        <b-col sm="6">
          <b-form-group
            description="This is the title that will be displayed in the demonstration page."
          >
            <h5>1. Demonstration Title</h5>
            <b-form-input
              id="input-formatter"
              v-model="form_data.title"
              placeholder="Enter the demonstration title"
              :state="titleState"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="form.currPage === 2" class="my-4">
        <b-col sm="6">
          <b-form-group
            description="These are the widgets that will be displayed in the demonstration page, and work as inputs for your algorithm. We recommend you create one for each parameter."
          >
            <h5>2. Inputs (Input Widgets)</h5>
          </b-form-group>
          <div
            class="component-card"
            v-for="comp in input_components"
            :key="comp.index"
          >
            <InputWidget
              :key="comp.index"
              :componentData="comp.compData"
              @update-input-creation-string="updateInputCreationString"
              @update-input-state="updateInputState"
            ></InputWidget>
            <b-button
              class="button primary component-field"
              @click="() => removeInputWidget(comp.index)"
            >
              Remove
            </b-button>
          </div>

          <b-dropdown
            id="dropdown-offset"
            offset="25"
            text="Add Input Widget"
            variant="primary"
            class="mb-4"
          >
            <b-dropdown-item @click="() => addInputWidget('Checkbox')"
              >Checkbox</b-dropdown-item
            >
            <b-dropdown-item @click="() => addInputWidget('FileUpload')"
              >File Uploader</b-dropdown-item
            >
            <b-dropdown-item @click="() => addInputWidget('MultiSelect')"
              >Multi Select</b-dropdown-item
            >
            <b-dropdown-item @click="() => addInputWidget('Radio')"
              >Radio</b-dropdown-item
            >
            <b-dropdown-item @click="() => addInputWidget('Slider')"
              >Slider (numbers)</b-dropdown-item
            >
            <b-dropdown-item @click="() => addInputWidget('TextArea')"
              >Text Area (multiple lines)</b-dropdown-item
            >
            <b-dropdown-item @click="() => addInputWidget('TextInput')"
              >Text Input (single line)</b-dropdown-item
            >
          </b-dropdown>
        </b-col>

        <b-col sm="6">
          <b-form-group
            description="These are placeholder for the elements that will be displayed in the demonstration page, and work as outputs for your algorithm. We recommend you create one for each type of output desired. These will display the result(s) of your algorithm once called."
          >
            <h5>3. Outputs (Placeholders for Display Elements)</h5>
          </b-form-group>

          <div
            class="component-card"
            v-for="comp in display_elements"
            :key="comp.index"
          >
            <DisplayElement
              :key="comp.index"
              :componentData="comp.compData"
              @update-display-element-creation-string="
                updateDisplayElementCreationString
              "
              @update-display-element-state="updateDisplayElementState"
            ></DisplayElement>
            <b-button
              class="button primary component-field"
              @click="() => removeDisplayElement(comp.index)"
            >
              Remove
            </b-button>
          </div>

          <b-button @click="() => addDisplayElement()" variant="primary">
            Add display element
          </b-button>
        </b-col>
      </b-row>

      <b-row v-if="form.currPage === 3" class="my-4">
        <b-col sm="4">
          <h5>4. Download template project</h5>
          <p>
            Once the title is <u>filled and valid</u> (inputs and outputs are
            not required) you may click the download button, in order to
            generate and download a template project. The file is a Zip folder
            containing a file (main.py) and a directory (Code).
          </p>
          <b-button
            @click="downloadTemplate"
            class="button primary my-4"
            style="width: 16em !important"
            :disabled="!titleState"
            >Download Project Template</b-button
          >
          <p class="important">
            Both of the files included in the template project are required for
            a valid demonstration submission. Their names and structure must
            also be kept the same.
          </p>
        </b-col>
        <b-col sm="4">
          <h5>5. Integration</h5>
          <p>
            Once the template project is downloaded and extracted, you may copy
            your code to the "Code" directory.
          </p>
          <p>
            In order to run the project, first you need to install the
            "streamlit" package (we recommend using pip). After that, call
            "streamlit run main.py" (from the project's root folder), and a
            server should start. Open the link, and you should see the page,
            with the title you wrote, and the input components you chose.
          </p>
          <p>
            The next step is to open main.py and call your algorithm while
            passing the input widgets and display elements variables inside the
            run function. Do not forget to import it first!
          </p>
          <p>
            You should adapt your algorithm to read the values from the input
            widgets and write their result(s) to the display elements'
            placeholders. All variables from the input widgets contain their
            selected value. As for the placeholders, they become display
            elements when called with the desired display element's method. The
            main.py file contains examples of these elements, and
            <a
              href="https://docs.streamlit.io/library/api-reference"
              target="_blank"
              rel="noopener noreferrer"
              >Streamlit's documentation</a
            >
            the full list of supported display elements.
          </p>
          <h6 class="separator-margin">Note</h6>
          <p>
            If your algorithm relies on windows to display its output, you
            should find alternatives to it. In the particular case of OpenCV's
            imshow method, you should just send the frames, instead of
            displaying them.
          </p>
        </b-col>
        <b-col sm="4">
          <h5>6. Upload project</h5>
          <p>
            When the demonstration is working as intended, compress it into a
            <u>Zip file</u>, and upload it.
          </p>
          <b-form-file
            v-model="project_folder"
            :state="uploadState"
            accept=".zip"
          ></b-form-file>
        </b-col>
      </b-row>

      <b-row v-if="form.currPage === 4" class="my-4">
        <b-col sm="6">
          <b-form-group
            description="This is used to create the container in which your algorithm will run. We recommend you choose the python version closest to the one the algorithm was developed on."
          >
            <h5>7. Python base image</h5>
            <b-form-select
              v-model="form_data.docker.python_base_image"
              :options="options"
            ></b-form-select>
          </b-form-group>

          <b-form-group
            description="This will be part of the url to access the demo page."
          >
            <h5>8. Service name</h5>
            <b-form-input
              id="input-formatter2"
              v-model="form_data.service_name"
              :state="serviceNameState"
              placeholder="Enter a service name"
            ></b-form-input>
          </b-form-group>
        </b-col>

        <b-col sm="6">
          <b-form-group
            description='This is the list of packages that will be installed. Should follow the pip "requirements" file format.'
          >
            <h5>9. Project Requirements</h5>
            <b-form-textarea
              id="textarea"
              v-model="form_data.requirements"
              placeholder="Enter the project dependencies"
              :state="requirementsState"
              rows="4"
              max-rows="32"
            ></b-form-textarea>
          </b-form-group>
          <h6 class="mt-4">Suggestion</h6>
          <p>
            If your project uses "opencv-python", we suggest you try to use
            <a
              href="https://pypi.org/project/opencv-python-headless/"
              target="_blank"
              rel="noopener noreferrer"
              >"opencv-python-headless"</a
            >
            instead, as it does not have as many dependencies and, therefore, is
            less problematic to run inside a container.
            <u
              >Note that this is a trimmed-down version of OpenCV and may not
              support every method your algorithm uses.</u
            >
          </p>
        </b-col>
      </b-row>

      <b-modal
        ref="confirmation-modal"
        centered
        hide-footer
        title="Are you sure?"
      >
        <p class="d-block text-center">
          Once you submit your demonstration, no changes can be made.
        </p>
        <div class="modal-button-section">
          <b-button
            class="button secondary conf-modal"
            variant="outline-danger"
            block
            @click="hideModal"
            >No, Go Back</b-button
          >
          <b-button
            class="button primary conf-modal"
            variant="outline-warning"
            block
            @click="onSubmit"
            >Yes, I am sure.</b-button
          >
        </div>
      </b-modal>

      <div class="button-section">
        <b-button
          :disabled="form.currPage === 1"
          @click="onPrevious"
          class="button secondary"
          >Previous</b-button
        >

        <b-button @click="onNext" class="button primary">
          {{
            this.form.currPage === this.form.numPages ? "Submit" : "Next"
          }}</b-button
        >
      </div>
    </b-container>
  </transition>
</template>

<script>
import { postNewDemo, downloadProjectTemplate } from "@/api";

import InputWidget from "@/components/InputWidget.vue";
import DisplayElement from "@/components/DisplayElement.vue";

import { inputWidgets } from "@/utils/InputWidgets.js";

export default {
  name: "DemoBuilder",
  components: {
    InputWidget,
    DisplayElement,
  },
  data() {
    return {
      show: true,
      input_components: [],
      input_components_counter: 0,
      display_elements: [],
      display_elements_counter: 0,
      options: [
        { value: "python:3.10", text: "Python 3.10" },
        { value: "python:3.9", text: "Python 3.9" },
        { value: "python:3.8", text: "Python 3.8" },
        { value: "python:3.7", text: "Python 3.7" },
        { value: "python:3.6", text: "Python 3.6" },
        { value: "python:3.5", text: "Python 3.5" },
        { value: "python:3.4", text: "Python 3.4" },
        { value: "python:3.3", text: "Python 3.3" },
        { value: "python:3.2", text: "Python 3.2" },
        { value: "python:2.7", text: "Python 2.7" },
      ],
      form: {
        currPage: 1,
        numPages: 4,
      },
      project_folder: null,
      form_data: {
        title: "",
        service_name: "",
        function_call_name: "",
        imports: [],
        input_widgets: [],
        display_elements: [],
        run_conditions: [],
        docker: {
          python_base_image: "python:3.10",
        },
        requirements: "",
        streamlit_config: {
          server_address: "localhost",
          send_usage_statistics: "false",
          server_port: 8502,
        },
      },
    };
  },
  methods: {
    onNext() {
      if (this.form.currPage === 1 && this.form_data.title === "") {
        this.makeToastBottomRight(
          true,
          "Demonstration title is required to proceed."
        );
        return;
      }

      if (this.form.currPage === 3 && this.project_folder === null) {
        this.makeToastBottomRight(
          true,
          "Please upload your project folder in order to proceed."
        );
        return;
      }

      if (this.form.currPage === this.form.numPages) {
        this.showModal();
      } else {
        this.form.currPage++;
      }
    },
    onPrevious() {
      if (this.form.currPage > 1) {
        this.form.currPage--;
      }
    },
    skipToUpload() {
      if (this.form.currPage === 1 && this.form_data.title === "") {
        this.makeToastBottomRight(
          true,
          "Demonstration title is required to upload."
        );
        return;
      }

      this.form.currPage = 3;
    },
    showModal() {
      this.$refs["confirmation-modal"].show();
    },
    hideModal() {
      this.$refs["confirmation-modal"].hide();
    },
    onSubmit() {
      this.updateInputComponentList();
      this.updateDisplayElementList();

      let formData = new FormData();
      // Adds uploaded file to formData
      formData.append("project", this.project_folder);
      // Converts form_data objects into a string and adds to formData
      formData.append("form_data", JSON.stringify(this.form_data));

      postNewDemo(formData)
        .then((res) => {
          this.makeToastBottomRight(false, res.data.message);
        })
        .catch((err) => {
          const displayMessage = err.response.data.message
            ? err.response.data.message
            : err.response.data.detail
            ? err.response.data.detail
            : "An error occurred. Try again.";
          this.makeToastBottomRight(true, displayMessage);
        });

      this.hideModal();
    },
    makeToastBottomRight(isError, message) {
      const variant = isError ? "danger" : "success";

      this.$bvToast.toast(message, {
        title: isError ? "Error" : "Success",
        toaster: "b-toaster-bottom-right",
        solid: true,
        variant: variant,
        appendToast: false,
      });
    },
    downloadTemplate() {
      this.updateInputComponentList();
      this.updateDisplayElementList();

      downloadProjectTemplate(this.form_data)
        .then((res) => {
          let blob = new Blob([res.data], {
            type: "application/x-zip-compressed",
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `TemplateProject.zip`;
          link.click();
          link.remove();
        })
        .catch((err) => {
          const displayMessage =
            err.response.status === 403
              ? "Authentication credentials were not provided. Please log in and try again (link in the first phase of this process)."
              : "Error making template project. Make sure that you filled in the previous form fields and try again.";

          this.makeToastBottomRight(true, displayMessage);
        });
    },
    addInputWidget(typeString) {
      let obj = {
        index: this.input_components_counter,
        value: "",
        compData: {
          id: this.input_components_counter,
          data: {},
        },
        valid: false,
      };

      switch (typeString) {
        case "FileUpload": {
          obj.compData["compTitle"] = "File Uploader";
          obj.compData["functionName"] = "file_uploader";
          obj.compData["fields"] = inputWidgets.fileUploader;

          this.input_components.push(obj);
          break;
        }
        case "TextInput": {
          obj.compData["compTitle"] = "Text Input";
          obj.compData["functionName"] = "text_input";
          obj.compData["fields"] = inputWidgets.textInput;

          this.input_components.push(obj);
          break;
        }
        case "TextArea": {
          obj.compData["compTitle"] = "Text Area";
          obj.compData["functionName"] = "text_area";
          obj.compData["fields"] = inputWidgets.textArea;

          this.input_components.push(obj);
          break;
        }
        case "Slider": {
          obj.compData["compTitle"] = "Slider";
          obj.compData["functionName"] = "slider";
          obj.compData["fields"] = inputWidgets.slider;

          this.input_components.push(obj);
          break;
        }
        case "Checkbox": {
          obj.compData["compTitle"] = "Checkbox";
          obj.compData["functionName"] = "checkbox";
          obj.compData["fields"] = inputWidgets.checkbox;

          this.input_components.push(obj);
          break;
        }
        case "Radio": {
          obj.compData["compTitle"] = "Radio";
          obj.compData["functionName"] = "radio";
          obj.compData["fields"] = inputWidgets.radio;

          this.input_components.push(obj);
          break;
        }
        case "MultiSelect": {
          obj.compData["compTitle"] = "Multi Select";
          obj.compData["functionName"] = "multiselect";
          obj.compData["fields"] = inputWidgets.multiSelect;

          this.input_components.push(obj);
          break;
        }
        default: {
          // Nothing happens
          return;
        }
      }

      this.input_components_counter++;
    },
    removeInputWidget(index) {
      for (let i = 0; i < this.input_components.length; i++) {
        if (this.input_components[i].index === index) {
          this.input_components.splice(i, 1);
          break;
        }
      }
    },
    updateInputCreationString(info) {
      const { id, newValue } = info;

      for (let i = 0; i < this.input_components.length; i++) {
        if (this.input_components[i].index === id) {
          this.input_components[i].value = newValue;
        }
      }
    },
    updateInputState(info) {
      const { id, newValue } = info;

      for (let i = 0; i < this.input_components.length; i++) {
        if (this.input_components[i].index === id) {
          this.input_components[i].valid = newValue;
        }
      }
    },
    updateInputComponentList() {
      // Clears valid input widgets
      this.form_data.input_widgets = [];

      // Adds the valid ones to form_data.input_widgets
      this.input_components.forEach((comp) => {
        if (comp.valid) {
          this.form_data.input_widgets.push({ creation_code: comp.value });
        }
      });
    },
    updateDisplayElementList() {
      // Clears valid display elements
      this.form_data.display_elements = [];

      // Adds the valid ones to form_data.display_elements
      this.display_elements.forEach((comp) => {
        if (comp.valid) {
          this.form_data.display_elements.push({ creation_code: comp.value });
        }
      });
    },
    addDisplayElement() {
      let obj = {
        index: this.display_elements_counter,
        value: "",
        compData: {
          id: this.display_elements_counter,
          compTitle: "Display Element Placeholder",
          data: {},
        },
        valid: false,
      };

      this.display_elements.push(obj);
      this.display_elements_counter++;
    },
    updateDisplayElementCreationString(info) {
      const { id, newValue } = info;

      for (let i = 0; i < this.display_elements.length; i++) {
        if (this.display_elements[i].index === id) {
          this.display_elements[i].value = newValue;
        }
      }
    },
    updateDisplayElementState(info) {
      const { id, newValue } = info;

      for (let i = 0; i < this.display_elements.length; i++) {
        if (this.display_elements[i].index === id) {
          this.display_elements[i].valid = newValue;
        }
      }
    },
    removeDisplayElement(index) {
      for (let i = 0; i < this.display_elements.length; i++) {
        if (this.display_elements[i].index === index) {
          this.display_elements.splice(i, 1);
          break;
        }
      }
    },
  },
  computed: {
    titleState() {
      const titleRegex = new RegExp("^[a-zA-Z0-9 !?<>#*]+$");
      return titleRegex.test(this.form_data.title);
    },
    uploadState() {
      return this.project_folder !== null;
    },
    serviceNameState() {
      const serviceNameRegex = new RegExp("^[a-zA-Z0-9-]+$");
      return serviceNameRegex.test(this.form_data.service_name);
    },
    requirementsState() {
      const requirementsRegex = new RegExp("^[a-zA-Z0-9-_<=>~.\\r\\n]+$");
      return (
        requirementsRegex.test(this.form_data.requirements) ||
        this.form_data.requirements === ""
      );
    },
  },
};
</script>

<style lang="scss">
h5 {
  text-align: start !important;
}

.progress-bar {
  background-color: $primary-color !important;
}

.step-progress {
  width: 100%;
  margin: auto;
  margin-top: 1em;
  background-color: rgba($color: $secondary-color, $alpha: 0.5) !important;
}

.button {
  width: 6em !important;
  border: 2px solid $primary-color !important;
}

.button.primary {
  background-color: $primary-color !important;
  color: white;
  font-weight: 500 !important;
}

.button.secondary {
  background-color: #00000000;
  color: $primary-color;
  font-weight: 500 !important;

  margin-right: 1em;
}

.button-section {
  margin-top: 0.5em;
}

.skip-section {
  display: flex;
  flex-direction: row;

  align-items: center;
  gap: 1em;
}

.button.primary.conf-modal,
.button.secondary.conf-modal {
  max-width: 40% !important;
  width: 12em !important;
}

.modal-button-section {
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: flex-end;
  justify-content: center;
}

.important {
  font-weight: 400 !important;
}

.separator-margin {
  margin-top: 3em;
}

.component-card {
  border: 2px solid $primary-color;
  border-radius: 8px;
  margin: 1em 0px;
  padding: 0.5em 0.5em;
}

.component-card > * {
  margin-top: 0.5em;
}

.component-card:first-child {
  margin-top: 0em;
}

.component-field {
  margin-top: 0.5em;
}
</style>
